import {Entity} from '@s-ui/domain'

import {URL_VEHICLE_CARD_FALLBACK} from '../../config/coches/ads.js'
import {SubcriptionsInfoAdValueObject} from '../ValueObjects/SubcriptionsInfoAdValueObject.js'

const isUndefined = value => value === undefined
const addProperty = property => !isUndefined(property) && property
const IMG_TYPE = 'IMAGE'

/**
 * @implements Entity
 * Motor Ad (Vehicle) Entity
 */
export default class AdEntityLegacy extends Entity {
  /**
   * @constructor
   * @param {Object} deps
   * @param {Array} deps.availableColors
   * @param {Number} deps.priceAverageIndicator
   * @param {Number} deps.bodyTypeId
   * @param {Object} deps.config
   * @param {String} deps.creationDate
   * @param {String} deps.color
   * @param {Number} deps.colorId
   * @param {String} deps.description
   * @param {String} deps.environmentalLabel
   * @param {String} deps.externalAdId
   * @param {Object} deps.favoriteData
   * @param {Number} deps.financedPrice
   * @param {Object} deps.financingInfo
   * @param {String} deps.fuelType
   * @param {Number} deps.fuelTypeId
   * @param {Boolean} deps.hasDiscount
   * @param {Boolean} deps.hasOfficialWarranty
   * @param {Boolean} deps.hasStock
   * @param {Boolean} deps.hasWarranty
   * @param {Number} deps.hp
   * @param {Number} deps.id
   * @param {String} deps.img
   * @param {Boolean} deps.includesTaxes
   * @param {Boolean} deps.isCertified
   * @param {Boolean} deps.isFavorite
   * @param {Boolean} deps.isFinanced
   * @param {Boolean} deps.isProfessional
   * @param {Boolean} deps.isTop - {motos-only param for ad visibility}
   * @param {Boolean} deps.isTradable
   * @param {Boolean} deps.isUrgent - {motos-only param for ad visibility}
   * @param {Number} deps.km
   * @param {location} deps.location
   * @param {String} deps.make
   * @param {Number} deps.makeId
   * @param {String} deps.model
   * @param {Number} deps.modelId
   * @param {Number} deps.monthlyFee
   * @param {Number} deps.monthlySubscriptionFee
   * @param {Object} deps.offerType
   * @param {Number} deps.offerType.id
   * @param {String} deps.offerType.literal
   * @param {Object} deps.pack
   * @param {Number} deps.pack.legacyId
   * @param {String} deps.pack.type
   * @param {String} deps.phone
   * @param {Array} deps.photos
   * @param {String} deps.plateRegistrationDate
   * @param {Number} deps.price
   * @param {Object} deps.priceDropData
   * @param {String} deps.province
   * @param {Number} deps.provinceId
   * @param {String} deps.publicationDate
   * @param {Number} deps.priceRankIndicator
   * @param {Number} deps.specificFuelTypeId
   * @param {Array} deps.subscriptionPriceOptions
   * @param {String} deps.subscriptionVehicleState
   * @param {Number} deps.taxTypeId
   * @param {String} deps.title
   * @param {String} deps.transmissionType
   * @param {Number} deps.transmissionTypeId
   * @param {Number} deps.traction
   * @param {String} deps.url
   * @param {String} deps.version
   * @param {String} deps.versionId
   * @param {Object} deps.videos
   * @param {Number} deps.warrantyId
   * @param {Number} deps.warrantyMonths
   * @param {String} deps.warrantyText
   * @param {Number} deps.year
   */

  static create({
    availableColors,
    priceAverageIndicator,
    bodyTypeId,
    category1Id,
    color,
    colorId,
    commitmentMonthlyOptions,
    creationDate,
    description,
    environmentalLabel,
    externalAdId,
    favoriteData,
    financedPrice,
    financingInfo,
    fuelType,
    fuelTypeId,
    hasDiscount,
    hasNoCommitmentOption,
    hasOfficialWarranty,
    hasOnlineFinancing,
    hasReservation,
    hasStock,
    hasWarranty,
    hidePhone,
    hp,
    id,
    img,
    includesTaxes,
    isCertified,
    isFavorite,
    isFinanced,
    isProfessional,
    isTop,
    isTradable,
    isUrgent,
    isSubscription,
    km,
    kmIncluded,
    kmIncludedPeriod,
    location,
    make,
    makeId,
    minimumMonthlyFee,
    model,
    modelId,
    monthlyFee,
    monthlySubscriptionCommitment,
    monthlySubscriptionFee,
    offer,
    offerType,
    pack,
    phone,
    photos,
    plateRegistrationDate,
    price,
    priceDrop,
    priceDropData,
    province,
    provinceId,
    publicationDate,
    priceRankIndicator,
    seller,
    specificFuelTypeId,
    subscriptionBusinessAdInfo,
    subscriptionPriceOptions,
    subscriptionVehicleState,
    targetBuyer,
    taxTypeId,
    title,
    transmissionType,
    transmissionTypeId,
    traction,
    url,
    version,
    versionId,
    videos = [],
    warrantyId,
    warrantyMonths,
    warrantyText,
    year
  }) {
    return new AdEntityLegacy({
      availableColors,
      priceAverageIndicator,
      bodyTypeId,
      category1Id,
      color,
      colorId,
      commitmentMonthlyOptions,
      creationDate,
      description,
      environmentalLabel,
      externalAdId,
      favoriteData,
      financedPrice,
      financingInfo,
      fuelType,
      fuelTypeId,
      hasDiscount,
      hasNoCommitmentOption,
      hasOfficialWarranty,
      hasOnlineFinancing,
      hasReservation,
      hasStock,
      hasWarranty,
      hidePhone,
      hp,
      id,
      img,
      includesTaxes,
      isCertified,
      isFavorite,
      isFinanced,
      isProfessional,
      isTop,
      isTradable,
      isSubscription,
      isUrgent,
      km,
      kmIncluded,
      kmIncludedPeriod,
      location,
      make,
      makeId,
      minimumMonthlyFee,
      model,
      modelId,
      monthlyFee,
      monthlySubscriptionCommitment,
      monthlySubscriptionFee,
      offer,
      offerType,
      pack,
      phone,
      photos,
      plateRegistrationDate,
      price,
      priceDrop,
      priceDropData,
      province,
      provinceId,
      publicationDate,
      priceRankIndicator,
      seller,
      specificFuelTypeId,
      subscriptionBusinessAdInfo,
      subscriptionPriceOptions,
      subscriptionVehicleState,
      targetBuyer,
      taxTypeId,
      title,
      transmissionType,
      transmissionTypeId,
      traction,
      url,
      version,
      versionId,
      videos,
      warrantyId,
      warrantyMonths,
      warrantyText,
      year
    })
  }

  static createFromRecommendedAd({ad}) {
    const financingInfoValueObject = SubcriptionsInfoAdValueObject.create({
      commitments: ad.subscriptionData?.commitmentsConfigurations?.otherOptions || [],
      price: ad.price,
      selectedMonthlyPrice: ad.subscriptionData?.commitmentsConfigurations?.selected?.monthlyPrice
    })

    const [firstImg] = ad.resources?.filter(resource => resource.type === IMG_TYPE).map(resource => resource?.url) || []

    return new AdEntityLegacy({
      ...ad,
      img: firstImg,
      ...(financingInfoValueObject.hasMonthlySubscriptionFee && {
        monthlySubscriptionFee: financingInfoValueObject.monthlySubscriptionFee
      }),
      price: ad.price?.amount || 0,
      videos: ad.videos || []
    })
  }

  static IMAGE_CROP_PARAM = '359x269cut'

  _getImgUrl = () => this._img && `${this._img}/${AdEntityLegacy.IMAGE_CROP_PARAM}/`

  _hasUrl = () => typeof this._url === 'string'

  _getFallbackUrl = () => `${URL_VEHICLE_CARD_FALLBACK}/${this._id}/`

  _getUrl = () => this._url || this._getFallbackUrl()

  getId = () => this._id

  getMake = () => this._make

  getMakeId = () => this._makeId

  getModel = () => this._model

  getModelId = () => this._modelId

  getFuelTypeId = () => this._fuelTypeId

  getBodyTypeId = () => this._bodyTypeId

  getProvinceId = () => this._provinceId

  getTitle = () => this._title

  getTransmissionTypeId = () => this._transmissionTypeId

  getVersion = () => this._version

  getVersionId = () => this._versionId

  getYear = () => this._year

  get value() {
    return {
      ...addProperty({availableColors: this._availableColors}),
      ...addProperty({priceAverageIndicator: this._priceAverageIndicator}),
      bodyTypeId: this._bodyTypeId,
      ...addProperty({color: this._color}),
      ...addProperty({colorId: this._colorId}),
      ...addProperty({
        commitmentMonthlyOptions: this._commitmentMonthlyOptions
      }),
      creationDate: this._creationDate,
      description: this._description,
      externalAdId: this._externalAdId,
      ...addProperty({
        favoriteData: this._favoriteData
      }),
      ...addProperty({environmentalLabel: this._environmentalLabel}),
      ...addProperty({
        financedPrice: this._financedPrice
      }),
      ...addProperty({
        financingInfo: this._financingInfo
      }),
      fuelType: this._fuelType,
      fuelTypeId: this._fuelTypeId,
      ...addProperty({hasDiscount: this._hasDiscount}),
      ...addProperty({
        hasNoCommitmentOption: this._hasNoCommitmentOption
      }),
      ...addProperty({
        hasOfficialWarranty: this._hasOfficialWarranty
      }),
      ...addProperty({hasOnlineFinancing: this._hasOnlineFinancing}),
      ...addProperty({hasReservation: this._hasReservation}),
      ...addProperty({hasStock: this._hasStock}),
      ...addProperty({hasWarranty: this._hasWarranty}),
      ...addProperty({hidePhone: this._hidePhone}),
      ...addProperty({hp: this._hp}),
      id: this._id,
      img: this._img,
      imgUrl: this._getImgUrl(),
      includesTaxes: this._includesTaxes,
      ...addProperty({isCertified: this._isCertified}),
      ...addProperty({isFavorite: this._isFavorite}),
      ...addProperty({isFinanced: this._isFinanced}),
      ...addProperty({isTradable: this._isTradable}),
      ...addProperty({isTop: this._isTop}),
      ...addProperty({isUrgent: this._isUrgent}),
      ...addProperty({
        isProfessional: this._isProfessional
      }),
      ...addProperty({
        isSubscription: this._isSubscription
      }),
      isUrlSemantic: this._hasUrl(),
      km: this._km,
      location: this._location,
      ...addProperty({make: this._make}),
      ...addProperty({makeId: this._makeId}),
      ...addProperty({model: this._model}),
      ...addProperty({modelId: this._modelId}),
      ...addProperty({minimumMonthlyFee: this._minimumMonthlyFee}),
      ...addProperty({monthlyFee: this._monthlyFee}),
      ...addProperty({monthlySubscriptionFee: this._monthlySubscriptionFee}),
      ...addProperty({
        monthlySubscriptionCommitment: this._monthlySubscriptionCommitment
      }),
      ...addProperty({kmIncluded: this._kmIncluded}),
      ...addProperty({kmIncludedPeriod: this._kmIncludedPeriod}),
      ...addProperty({pack: this._pack}),
      price: this._price,
      priceDrop: this._priceDrop,
      ...addProperty({priceDropData: this._priceDropData}),
      url: this._getUrl(),
      ...addProperty({offer: this._offer}),
      ...addProperty({offerType: this._offerType}),
      ...addProperty({category1Id: this._category1Id}),
      phone: this._phone,
      photos: this._photos,
      ...addProperty({plateRegistrationDate: this._plateRegistrationDate}),
      ...addProperty({province: this._province}),
      provinceId: this._provinceId,
      publicationDate: this._publicationDate,
      ...addProperty({priceRankIndicator: this._priceRankIndicator}),
      ...addProperty({
        specificFuelTypeId: this._specificFuelTypeId
      }),
      ...addProperty({
        subscriptionBusinessAdInfo: this._subscriptionBusinessAdInfo
      }),
      ...addProperty({
        subscriptionPriceOptions: this._subscriptionPriceOptions
      }),
      ...addProperty({
        subscriptionVehicleState: this._subscriptionVehicleState
      }),
      ...addProperty({
        targetBuyer: this._targetBuyer
      }),
      taxTypeId: this._taxTypeId,
      title: this._title,
      ...addProperty({transmissionType: this._transmissionType}),
      ...addProperty({transmissionTypeId: this._transmissionTypeId}),
      traction: this._traction,
      ...addProperty({version: this._version}),
      ...addProperty({versionId: this._versionId}),
      videos: this._videos,
      ...addProperty({warrantyId: this._warrantyId}),
      ...addProperty({
        warrantyMonths: this._warrantyMonths
      }),
      ...addProperty({
        warrantyText: this._warrantyText
      }),
      year: this._year,
      ...addProperty({seller: this._seller})
    }
  }

  /**
   * @return {Object}
   */
  toJSON() {
    return {
      ...addProperty({availableColors: this._availableColors}),
      ...addProperty({priceAverageIndicator: this._priceAverageIndicator}),
      bodyTypeId: this._bodyTypeId,
      ...addProperty({color: this._color}),
      ...addProperty({colorId: this._colorId}),
      ...addProperty({
        commitmentMonthlyOptions: this._commitmentMonthlyOptions
      }),
      creationDate: this._creationDate,
      description: this._description,
      externalAdId: this._externalAdId,
      ...addProperty({
        favoriteData: this._favoriteData
      }),
      ...addProperty({environmentalLabel: this._environmentalLabel}),
      ...addProperty({hp: this._hp}),
      ...addProperty({
        financedPrice: this._financedPrice
      }),
      ...addProperty({
        financingInfo: this._financingInfo
      }),
      fuelType: this._fuelType,
      fuelTypeId: this._fuelTypeId,
      ...addProperty({hasDiscount: this._hasDiscount}),
      ...addProperty({
        hasNoCommitmentOption: this._hasNoCommitmentOption
      }),
      ...addProperty({
        hasOfficialWarranty: this._hasOfficialWarranty
      }),
      hasOnlineFinancing: this._hasOnlineFinancing,
      hasReservation: this._hasReservation,
      ...addProperty({hidePhone: this._hidePhone}),
      ...addProperty({hp: this._hp}),
      ...addProperty({hasStock: this._hasStock}),
      ...addProperty({hasWarranty: this._hasWarranty}),
      id: this._id,
      img: this._img,
      imgUrl: this._getImgUrl(),
      includesTaxes: this._includesTaxes,
      ...addProperty({isCertified: this._isCertified}),
      ...addProperty({isFavorite: this._isFavorite}),
      ...addProperty({isFinanced: this._isFinanced}),
      ...addProperty({isTradable: this._isTradable}),
      ...addProperty({isTop: this._isTop}),
      ...addProperty({isUrgent: this._isUrgent}),
      ...addProperty({
        isProfessional: this._isProfessional
      }),
      ...addProperty({
        isSubscription: this._isSubscription
      }),
      isUrlSemantic: this._hasUrl(),
      km: this._km,
      location: this._location,
      ...addProperty({make: this._make}),
      ...addProperty({makeId: this._makeId}),
      ...addProperty({model: this._model}),
      ...addProperty({modelId: this._modelId}),
      ...addProperty({minimumMonthlyFee: this._minimumMonthlyFee}),
      ...addProperty({monthlyFee: this._monthlyFee}),
      ...addProperty({monthlySubscriptionFee: this._monthlySubscriptionFee}),
      ...addProperty({
        monthlySubscriptionCommitment: this._monthlySubscriptionCommitment
      }),
      ...addProperty({kmIncluded: this._kmIncluded}),
      ...addProperty({kmIncludedPeriod: this._kmIncludedPeriod}),
      ...addProperty({pack: this._pack}),
      price: this._price,
      priceDrop: this._priceDrop,
      ...addProperty({priceDropData: this._priceDropData}),
      url: this._getUrl(),
      ...addProperty({offer: this._offer}),
      ...addProperty({offerType: this._offerType}),
      ...addProperty({category1Id: this._category1Id}),
      phone: this._phone,
      photos: this._photos,
      ...addProperty({plateRegistrationDate: this._plateRegistrationDate}),
      ...addProperty({province: this._province}),
      provinceId: this._provinceId,
      publicationDate: this._publicationDate,
      ...addProperty({priceRankIndicator: this._priceRankIndicator}),
      ...addProperty({
        specificFuelTypeId: this._specificFuelTypeId
      }),
      ...addProperty({
        subscriptionBusinessAdInfo: this._subscriptionBusinessAdInfo
      }),
      ...addProperty({
        subscriptionPriceOptions: this._subscriptionPriceOptions
      }),
      ...addProperty({
        subscriptionVehicleState: this._subscriptionVehicleState
      }),
      ...addProperty({
        targetBuyer: this._targetBuyer
      }),
      taxTypeId: this._taxTypeId,
      title: this._title,
      ...addProperty({transmissionType: this._transmissionType}),
      ...addProperty({transmissionTypeId: this._transmissionTypeId}),
      traction: this._traction,
      ...addProperty({version: this._version}),
      ...addProperty({versionId: this._versionId}),
      videos: this._videos,
      ...addProperty({warrantyId: this._warrantyId}),
      ...addProperty({
        warrantyMonths: this._warrantyMonths
      }),
      ...addProperty({
        warrantyText: this._warrantyText
      }),
      year: this._year,
      ...addProperty({seller: this._seller})
    }
  }
}
